/**
 * Dimension Detection Utilities
 *
 * Shared utilities for reliably detecting element dimensions
 * across different browsers and scenarios.
 */

export interface ElementDimensions {
  width: number;
  height: number;
}

/**
 * Get element dimensions using multiple fallback methods.
 * Tries different dimension detection methods in order of reliability.
 */
export function getElementDimensions(element: HTMLElement): ElementDimensions {
  const methods = [
    () => ({ width: element.offsetWidth, height: element.offsetHeight }),
    () => ({ width: element.clientWidth, height: element.clientHeight }),
    () => {
      const rect = element.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    },
    () => {
      const computed = window.getComputedStyle(element);
      return {
        width: parseFloat(computed.width) || 0,
        height: parseFloat(computed.height) || 0
      };
    }
  ];

  for (const method of methods) {
    try {
      const dimensions = method();
      if (dimensions.width > 0 && dimensions.height > 0) {
        return dimensions;
      }
    } catch {
      // Continue to next method
    }
  }

  // If all methods fail, return zeros
  return { width: 0, height: 0 };
}

/**
 * Validate that dimensions are reasonable for mask application.
 */
export function validateDimensions(dimensions: ElementDimensions): boolean {
  // Must have positive dimensions
  if (dimensions.width <= 0 || dimensions.height <= 0) {
    return false;
  }

  // Must be reasonably sized (not too small)
  if (dimensions.width < 50 || dimensions.height < 50) {
    return false;
  }

  return true;
}

